import { ApplicationElement, html } from '../application_element/element.js';
import style from './style.lit.css';

/**
 * Smart search box field
 *
 * Featuring:
 * - [x] Clear button when content is present
 * - [x] Restore to default value on focus out or escape
 * - [x] Exit and restore state on ESC
 * - [x] Option to not restore state on focus out or ESC
 * - [x] Focusable restore clear button
 * - [x] Disabled submit when input is empty
 * - [x] Keep main focus while tabbing through inner children
 *
 * @prop hasValue Internal state - depending on presence of input value
 * @prop preventRestore - do not restore value on focus out or escape
 *
 *
 */
export default class Searchbox extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    hasValue: { state: true },
    preventRestore: { type: Boolean },
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('focusin', this);
    this.addEventListener('focusout', this);
    this.addEventListener('keyup', this);
  }

  willUpdate(changedProperties) {
    super.willUpdate();

    if (changedProperties.has('hasValue')) {
      this.showHideSubmitButton();
    }
  }

  handleEvent(e) {
    e.type === 'focusin' && this.onFullFocusIn(e);
    e.type === 'focusout' && this.onFullFocusOut(e);
    e.type === 'keyup' && this.onKeyup(e);
  }

  onInput(e) {
    this.detectValuePresence(e.target);
  }

  onSlotChange() {
    this.detectValuePresence();
  }

  onFullFocusOut() {
    this.timeout = setTimeout(() => {
      !this.preventRestore && this.changed && this.reset();
    }, 0);
  }

  onKeyup(e) {
    e.key === 'Escape' && !this.preventRestore && this.reset();
  }

  onFullFocusIn() {
    clearTimeout(this.timeout);
  }

  reset() {
    this.resetInput();
    this.detectValuePresence();
  }

  resetInput() {
    this.input.value = this.input.defaultValue;
  }

  detectValuePresence(input = this.input) {
    this.hasValue = input.value.trim();
  }

  showHideSubmitButton() {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = !this.hasValue;
  }

  clearInput(e) {
    e.preventDefault();
    this.input.value = '';
    this.detectValuePresence();
    this.input.focus();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @internal */
  get changed() {
    const { defaultValue, value } = this.input;
    return defaultValue !== value;
  }

  /** @internal */
  get input() {
    return this.slot.assignedElements({ flatten: true, selector: 'input' })[0];
  }

  /** @internal */
  get submitButton() {
    return this.submitSlot.assignedElements({
      flatten: true,
      selector: '[type="submit"]',
    })[0];
  }

  /** @internal */
  get clearButton() {
    return this.shadowRoot.querySelector('slot[name="clear"]');
  }

  /** @internal */
  get slot() {
    return this.shadowRoot.querySelector('slot');
  }

  /** @internal */
  get submitSlot() {
    return this.shadowRoot.querySelector('slot[name=submit]');
  }

  render() {
    return html`
      <slot @input=${this.onInput} @slotchange=${this.onSlotChange}></slot>
      <slot name="clear" @click=${this.clearInput} ?hidden=${!this.hasValue}>
        <button>×</button>
      </slot>
      <slot name="submit"></slot>
    `;
  }
}

customElements.get('ef-searchbox') ||
  customElements.define('ef-searchbox', Searchbox);
